import {
    Box,
    Button,
    Chip,
    Grid,
    Paper,
    Typography,
    useMediaQuery
} from "@material-ui/core";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import React from "react";
import {
    createStyles,
    Theme,
    makeStyles,
    useTheme
} from "@material-ui/core/styles";
import Timeline from "@material-ui/lab/Timeline";
import { useApiCache } from "customHooks/useApi";
import { useAuthState } from "_ReactContext/AuthContext";
import { useTranslation } from "react-i18next";
import ApplicantHistoryNewEditCommentModal from "./ApplicantHistoryNewEditCommentModal";
import ApplicantHistoryRow from "./ApplicantHistoryRow";
import { useGetSizeByHistoryType } from "./ApplicantHistoryHelper";
import useApplicantViewContext from "../useApplicantViewContext";
import ApplicantHistoryMenuFilter from "./ApplicantHistoryMenuFilter";
import { Alert } from "@material-ui/lab";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        gridContainer: {
            height: "90%",
            backgroundColor: "transparent"
        },
        container: {
            display: "flex",
            flex: "1 1 auto",
            flexDirection: "column",
            height: "100%",
            backgroundColor: theme.palette.backgroundGrey.main
        },
        paperContainer: {
            margin: theme.spacing(2),
            padding: theme.spacing(2)
        },
        timeline: {
            // Better mobile support for timeline alignment
            [theme.breakpoints.down("sm")]: {
                padding: theme.spacing(0, 1)
            }
        },
        chipCard: {
            marginRight: theme.spacing(0.5),
            marginLeft: 8,
            borderRadius: 6
        },
        alertNoResults: {
            margin: theme.spacing(2)
        }
    })
);

const ApplicantHistory = (props: any) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { getSizeByHistoryType } = useGetSizeByHistoryType();
    const [data] = useApplicantViewContext();
    const { company } = useAuthState();
    const { applicantId } = data;
    const [modalCommentOpen, setModalCommentOpen] = React.useState<{
        open: boolean;
        editData?: {
            commentId: string;
            historyData: Compleo.IObject;
        };
    }>({ open: false });

    const [filterType, setFilterType] = React.useState<
        CompleoShared.Common.ApplicantHistoryType | "all"
    >("all");
    const stageId = data?.jobDataInsideJob
        ? (data.applicantData?.Jobs || []).filter(
              (job: Compleo.IObject) =>
                  job.JobId === data?.jobDataInsideJob?.jobId
          )?.[0]?.Stage?.id
        : undefined;

    const [displayLimit, setDisplayLimit] = React.useState(20);

    const [tLocal, i18n, readyTranslation] = useTranslation(
        "APPLICANTVIEWHISTORY",
        {
            useSuspense: false
        }
    );
    const listAddress = `/applicant/applicanthistorylist`;
    const postData = {
        applicantId: applicantId,
        companyId: company.companyId,
        pageSize: 5000,
        page: 1,
        sizeProcessLocal: 0,
        startArray: 0,
        type: filterType
    };
    const [listHistory, updateListHistory, , isFetching] = useApiCache(
        listAddress,
        "post",
        postData,
        false,
        1000 * 60
    );

    React.useEffect(() => {
        updateListHistory(postData);
    }, [applicantId, filterType]);

    // Derive historyData directly from API response via useMemo.
    // Previously, a useState + useEffect pattern was used, but the effect dependency
    // `(listHistory.response?.data?.data || []).length` failed to detect content
    // changes when the filtered result had the same number of items as the previous
    // result, leaving stale data on screen (issue #289 re-opened after PR #290).
    const historyData = React.useMemo<Compleo.IObject[]>(() => {
        if (listHistory.status === "success" && !isFetching) {
            return listHistory.response?.data?.data || [];
        }
        return [];
    }, [listHistory.response?.data?.data, listHistory.status, isFetching]);

    // Computed from current historyData and pagination cursor.
    const hasMore = displayLimit < historyData.length;

    // Reset pagination whenever the filter or applicant changes.
    React.useEffect(() => {
        setDisplayLimit(20);
    }, [filterType, applicantId]);

    if (readyTranslation) {
        return (
            <Grid container item className={classes.gridContainer}>
                <Box className={classes.container}>
                    <Paper className={classes.paperContainer}>
                        <Grid container>
                            <Grid item xs={12} sm={8}>
                                <Typography variant="body1" component="span">
                                    {tLocal("title")}
                                </Typography>
                                <Chip
                                    className={classes.chipCard}
                                    style={{
                                        backgroundColor: "salmon",
                                        color: "white"
                                    }}
                                    size="small"
                                    label={
                                        filterType === "all"
                                            ? tLocal("labelAll")
                                            : tLocal(
                                                  `ApplicantHistoryType_${filterType}`
                                              )
                                    }
                                />
                            </Grid>

                            <Grid item xs sm container justify="flex-end">
                                {!data.readOnly && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="primary"
                                        // disabled={!ready}
                                        onClick={() =>
                                            setModalCommentOpen({
                                                open: true
                                            })
                                        }
                                    >
                                        {tLocal("buttonNewComment")}
                                    </Button>
                                )}
                                <ApplicantHistoryMenuFilter
                                    t={tLocal}
                                    filterType={filterType}
                                    setFilterType={setFilterType}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                    {historyData.length ? (
                        <Timeline
                            align={isMobile ? "left" : "left"}
                            className={classes.timeline}
                        >
                            {historyData
                                .slice(0, displayLimit)
                                .map((historyItem, index) => (
                                    <ApplicantHistoryRow
                                        key={index}
                                        data={{
                                            historyData: historyData,
                                            tLocal: tLocal,
                                            language: i18n.languages[0],
                                            setModalCommentOpen: setModalCommentOpen
                                        }}
                                        index={index}
                                        style={{
                                            height: getSizeByHistoryType(
                                                historyItem?.type
                                            )
                                        }}
                                    />
                                ))}
                            {hasMore && (
                                <Button
                                    onClick={() => {
                                        setDisplayLimit(
                                            (prev) => prev + 10
                                        );
                                    }}
                                    variant="outlined"
                                    color="primary"
                                    style={{ margin: "auto", marginTop: 24 }}
                                >
                                    {tLocal("showMore")}
                                </Button>
                            )}
                        </Timeline>
                    ) : (
                        <Alert
                            severity="info"
                            className={classes.alertNoResults}
                        >
                            {tLocal("noResuts")}
                        </Alert>
                    )}

                    {modalCommentOpen.open && (
                        <ApplicantHistoryNewEditCommentModal
                            open={modalCommentOpen.open}
                            onClose={() => setModalCommentOpen({ open: false })}
                            appicantViewDataProvider={data}
                            allowChangeJob={true}
                            stageId={stageId}
                            editData={modalCommentOpen.editData}
                        />
                    )}
                </Box>
            </Grid>
        );
    } else {
        return (
            <Grid container item className={classes.gridContainer}>
                <Box className={classes.container}>
                    <Paper className={classes.paperContainer}>
                        <Loading />
                    </Paper>
                </Box>
            </Grid>
        );
    }
};

export default ApplicantHistory;
